import VideoMeetings from './VideoMeetings';
import FileDrive from './FileDrive';
import TherapistChats from './TherapistChats';
import AdminNavigation from './AdminNavigation';
import ProfileRequests from './ProfileRequests';
import UsersTable from './UsersTable';
import ConsultationsTab from './ConsultationsTab';
import AgregatorApplications from './AgregatorApplications';
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';
import { useConfig } from '../../contexts/ConfigContext';
import { getDirectionTerms } from '../../utils/organizationHelpers';
import Modal from '../../components/Modal';
import SimpleForm from '../../components/SimpleForm';
import DirectionForm from '../../components/DirectionForm';
import UserEditForm from '../../components/UserEditForm';
import {
    Settings,
    Users,
    Layers,
    MapPin,
    BookOpen,
    FileText,
    BarChart3,
    Check,
    X,
    GraduationCap,
    Database,
    HardDrive,
    Bell,
    CreditCard,
    Upload,
    Video,
    Mail
} from 'lucide-react';
import './AdminPage.css';
import { formatDateShortRu } from '../../utils/dateUtils';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import { PAYKEEPER_STATUS_OPTIONS, labelPaykeeperStatus } from '../../utils/paykeeperPaymentStatusLabels';
import TherapyClientsTab from './TherapyClientsTab';
import ZachetkaGroupsTab from './ZachetkaGroupsTab';
import ZoomChannelsTab from './ZoomChannelsTab';
import MailingsTab from './MailingsTab';

// Вспомогательная функция для запросов с авторизацией
const fetchWithAuth = async (url, options = {}) => {
    const { method = 'GET', body, headers, ...rest } = options;
    const response = await axios({
        url,
        method,
        withCredentials: true,
        data: body ? JSON.parse(body) : undefined,
        headers,
        ...rest
    });
    return response.data;
};

const AdminPage = () => {
    const queryClient = useQueryClient();
    const { config } = useConfig();
    const isMag = config?.code === 'mag';
    const isNasledie = config?.code === 'nasledie';
    const directionTerms = getDirectionTerms(config?.code);
    const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('section') === 'therapy' ? 'therapySection' : new URLSearchParams(window.location.search).get('section')==='users'?'users':'stats');
    const [activeSubTab, setActiveSubTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'consultations'); // Для подвкладок в разделах
    const [, setExpandedSection] = useState(null); // Для развернутых разделов
    const [paymentsPage, setPaymentsPage] = useState(1);
    const [paymentsFilters, setPaymentsFilters] = useState({
        status: [],
        date_from: '',
        date_to: '',
        clientid: '',
        orderid: '',
        search: '',
        amount_from: '',
        amount_to: ''
    });

    const { data: stats, isLoading: statsLoading } = useQuery(
        'adminStats',
        () => fetchWithAuth(`${API_BASE_URL}/admin/stats`),
        { enabled: activeTab === 'stats' }
    );

    const { data: groups, isLoading: groupsLoading, refetch: refetchGroups } = useQuery(
        'adminGroups',
        () => fetchWithAuth(`${API_BASE_URL}/admin/groups`),
        { enabled: (activeTab === 'groupsSection' && (activeSubTab === 'applications' || activeSubTab === 'students' || activeSubTab === 'directions')) || activeTab === 'regions' }
    );

    const { data: applications, isLoading: applicationsLoading } = useQuery(
        'adminApplications',
        () => fetchWithAuth(`${API_BASE_URL}/admin/applications`),
        { enabled: activeTab === 'groupsSection' && activeSubTab === 'applications' }
    );

    const { data: users, isLoading: usersLoading, refetch: refetchUsers } = useQuery(
        'users',
        () => fetchWithAuth(`${API_BASE_URL}/users`),
        { enabled: activeTab === 'users' }
    );

    const { data: regions, isLoading: regionsLoading, refetch: refetchRegions } = useQuery(
        'regions',
        () => axios.get(`${API_BASE_URL}/regions/public`).then(res => res.data),
        { enabled: activeTab === 'regions' || (activeTab === 'groupsSection' && activeSubTab === 'directions') }
    );

    const { data: directions, isLoading: directionsLoading, refetch: refetchDirections } = useQuery(
        'directions',
        () => axios.get(`${API_BASE_URL}/directions/public`).then(res => res.data),
        { enabled: (activeTab === 'groupsSection' && activeSubTab === 'directions') || activeTab === 'regions' }
    );

    const { data: therapyTopics, isLoading: therapyTopicsLoading, refetch: refetchTherapyTopics } = useQuery(
        'therapyTopics',
        () => axios.get(`${API_BASE_URL}/therapy-topics`).then(res => res.data),
        { enabled: activeTab === 'therapySection' && activeSubTab === 'therapyTopics' }
    );

    const { data: qualifications, isLoading: qualificationsLoading, refetch: refetchQualifications } = useQuery(
        'qualifications',
        () => axios.get(`${API_BASE_URL}/qualifications`).then(res => res.data),
        { enabled: activeTab === 'therapySection' && activeSubTab === 'qualifications' }
    );

    const { data: students, isLoading: studentsLoading } = useQuery(
        'adminStudents',
        () => fetchWithAuth(`${API_BASE_URL}/admin/students`),
        { enabled: activeTab === 'groupsSection' && activeSubTab === 'students' }
    );

    const { data: consultationBookings, isLoading: bookingsLoading } = useQuery(
        'adminConsultationBookings',
        () => fetchWithAuth(`${API_BASE_URL}/admin/consultation-bookings`),
        { enabled: activeTab === 'therapySection' && activeSubTab === 'consultations' }
    );

    const { data: aggregatorClients, isLoading: aggregatorClientsLoading } = useQuery(
        'adminAggregatorClients',
        () => fetchWithAuth(`${API_BASE_URL}/admin/paykeeper/aggregator-clients`),
        { enabled: isMag && activeTab === 'therapySection' && activeSubTab === 'clients' }
    );

    const { data: rentalClients, isLoading: rentalClientsLoading } = useQuery(
        'adminRentalClients',
        () => fetchWithAuth(`${API_BASE_URL}/admin/paykeeper/rental-clients`),
        { enabled: isMag && activeTab === 'rentalClients' }
    );

    const { data: zachetkaGroups, isLoading: zachetkaGroupsLoading, error: zachetkaGroupsError } = useQuery(
        'adminZachetkaGroupsStudents',
        () => fetchWithAuth(`${API_BASE_URL}/admin/zachetka/groups-students`),
        {
            enabled: isMag && activeTab === 'zachetkaGroups',
            staleTime: 5 * 60 * 1000,
            retry: 1
        }
    );

    const {
        data: matchingApplications,
        isLoading: matchingApplicationsLoading,
        refetch: refetchMatchingApplications
    } = useQuery(
        'adminMatchingApplications',
        () => fetchWithAuth(`${API_BASE_URL}/admin/therapist-matching/applications`),
        { enabled: activeTab === 'therapySection' && activeSubTab === 'matchingApplications' }
    );

    const {
        data: matchingSettings,
        isLoading: matchingSettingsLoading,
        refetch: refetchMatchingSettings
    } = useQuery(
        'adminMatchingSettings',
        () => fetchWithAuth(`${API_BASE_URL}/admin/therapist-matching/settings`),
        { enabled: activeTab === 'therapySection' && activeSubTab === 'matchingApplications' }
    );

    const { data: backups, isLoading: backupsLoading, refetch: refetchBackups } = useQuery(
        'adminBackups',
        () => fetchWithAuth(`${API_BASE_URL}/admin/backups`),
        { enabled: activeTab === 'backups' }
    );

    const { data: notificationsLog, isLoading: notificationsLoading, refetch: refetchNotifications } = useQuery(
        'adminNotificationsLog',
        () => fetchWithAuth(`${API_BASE_URL}/admin/notifications-log?limit=200`),
        { enabled: activeTab === 'notifications' }
    );

    const paymentsQueryParams = new URLSearchParams({ limit: 50, page: paymentsPage });
    Object.entries(paymentsFilters).forEach(([k, v]) => {
        if (Array.isArray(v)) {
            if (v.length) paymentsQueryParams.set(k, v.join(','));
        } else if (v != null && String(v).trim()) {
            paymentsQueryParams.set(k, String(v).trim());
        }
    });
    const { data: paykeeperPayments, isLoading: paymentsLoading } = useQuery(
        ['adminPaykeeperPayments', paymentsPage, paymentsFilters],
        () => fetchWithAuth(`${API_BASE_URL}/admin/paykeeper/payments?${paymentsQueryParams.toString()}`),
        { enabled: activeTab === 'payments' && isMag }
    );

    useEffect(() => {
        if (isMag) return;
        if (activeTab === 'payments' || activeTab === 'rentalClients' || activeTab === 'zachetkaGroups') {
            setActiveTab('stats');
            setActiveSubTab(null);
            setExpandedSection(null);
        } else if (activeTab === 'therapySection' && activeSubTab === 'clients') {
            setActiveSubTab('consultations');
        }
    }, [activeTab, activeSubTab, isMag]);

    useEffect(() => {
        if (isNasledie) return;
        if (activeTab === 'zoom') {
            setActiveTab('stats');
            setActiveSubTab(null);
            setExpandedSection(null);
        }
    }, [activeTab, isNasledie]);

    useEffect(() => {
        if (activeTab !== 'payments' || !isMag) return;
        let cancelled = false;
        (async () => {
            try {
                await axios.post(
                    `${API_BASE_URL}/paykeeper/payments/refresh`,
                    {},
                    { withCredentials: true, timeout: 120000 }
                );
            } catch (err) {
                console.error(err);
                toast.error(err.response?.data?.message || 'Не удалось подтянуть платежи из PayKeeper');
            }
            if (!cancelled) {
                queryClient.invalidateQueries(['adminPaykeeperPayments']);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeTab, isMag, queryClient]);

    // Основные вкладки (PayKeeper — только MAG)
    const mainTabs = useMemo(() => [
        { id: 'stats', label: 'Статистика', icon: BarChart3 },
        { id: 'backups', label: 'Бекапы', icon: Database },
        { id: 'files', label: 'Файловая система', icon: HardDrive },
        { id: 'users', label: 'Пользователи', icon: Users },
        { id: 'groupsSection', label: 'Группы', icon: Layers, hasSubTabs: true },
        { id: 'therapySection', label: 'Терапия', icon: FileText, hasSubTabs: true },
        { id: 'regions', label: 'Регионы', icon: MapPin },
        { id: 'notifications', label: 'Логи уведомлений', icon: Bell },
        ...(isMag ? [
            { id: 'payments', label: 'Платежи', icon: CreditCard },
            { id: 'rentalClients', label: 'Клиенты Аренды', icon: Users },
            { id: 'zachetkaGroups', label: 'Группы и студенты', icon: GraduationCap }
        ] : []),
        ...(isNasledie ? [
            { id: 'zoom', label: 'Zoom', icon: Video },
            { id: 'mailings', label: 'Рассылки', icon: Mail }
        ] : []),
    ], [isMag, isNasledie]);

    // Подвкладки для раздела "Группы" (подраздел "Группы" убран — админ пользуется общим разделом групп)
    const groupsSubTabs = [
        { id: 'applications', label: 'Заявки', icon: FileText },
        { id: 'students', label: 'Студенты', icon: GraduationCap },
        { id: 'directions', label: directionTerms.many, icon: BookOpen }
    ];

    // Подвкладки для раздела "Терапия"
    const therapySubTabs = [
        { id: 'consultations', label: 'Консультации', icon: FileText },
        { id: 'matchingApplications', label: 'Заявки на подбор', icon: FileText },
        ...(isMag ? [{ id: 'videoMeetings', label: 'Видеовстречи', icon: Video }] : []),
        ...(isMag ? [{ id: 'therapistChats', label: 'Чаты с терапевтами', icon: FileText },{ id: 'profileRequests', label: 'Изменения профилей', icon: FileText },{ id: 'agregatorApplications', label: 'Заявки в агрегатор', icon: FileText }] : []),
        { id: 'therapyTopics', label: 'Темы-запрос', icon: FileText },
        { id: 'qualifications', label: 'Квалификации', icon: GraduationCap },
        ...(isMag ? [{ id: 'clients', label: 'Клиенты', icon: Users }] : [])
    ];

    const handleMainTabClick = (tabId) => {
        setActiveTab(tabId);
        setExpandedSection(tabId);
        setActiveSubTab(tabId==='groupsSection'?'applications':tabId==='therapySection'?'consultations':null);
    };

    const handleSubTabClick = (subTabId) => {
        setActiveSubTab(subTabId);
    };

    const navigateAdmin = (tabId, subTabId = null) => {
        if (tabId === 'groupsSection' || tabId === 'therapySection') {
            setActiveTab(tabId);
            setExpandedSection(tabId);
            setActiveSubTab(subTabId);
        } else {
            setActiveTab(tabId);
            setActiveSubTab(null);
            setExpandedSection(null);
        }
    };

    const renderContent = () => {
        // Если выбрана секция с подвкладками
        if (activeTab === 'groupsSection' && activeSubTab) {
            switch (activeSubTab) {
                case 'applications':
                    return <ApplicationsTab applications={applications} loading={applicationsLoading} isMag={isMag} />;
                case 'students':
                    return <StudentsTab data={students} loading={studentsLoading} />;
                case 'directions':
                    return <DirectionsTab directions={directions} loading={directionsLoading} refetch={refetchDirections} groups={groups} />;
                default:
                    return null;
            }
        }

        if (activeTab === 'therapySection' && activeSubTab) {
            switch (activeSubTab) {
                case 'videoMeetings':
                    return isMag ? <VideoMeetings /> : null;
                case 'consultations':
                    return <ConsultationsTab bookings={consultationBookings} loading={bookingsLoading} />;
                case 'agregatorApplications':
                    return isMag ? <AgregatorApplications /> : null;
                case 'therapistChats':
                    return isMag ? <TherapistChats /> : null;
                case 'profileRequests':
                    return isMag ? <ProfileRequests/> : null;
                case 'matchingApplications':
                    return (
                        <MatchingApplicationsTab
                            applications={matchingApplications}
                            applicationsLoading={matchingApplicationsLoading}
                            settings={matchingSettings}
                            settingsLoading={matchingSettingsLoading}
                            refetchApplications={refetchMatchingApplications}
                            refetchSettings={refetchMatchingSettings}
                        />
                    );
                case 'therapyTopics':
                    return <TherapyTopicsTab topics={therapyTopics} loading={therapyTopicsLoading} refetch={refetchTherapyTopics} />;
                case 'qualifications':
                    return <QualificationsTab qualifications={qualifications} loading={qualificationsLoading} refetch={refetchQualifications} />;
                case 'clients':
                    if (!isMag) return null;
                    return <TherapyClientsTab data={aggregatorClients} loading={aggregatorClientsLoading} />;
                default:
                    return null;
            }
        }

        // Основные вкладки
        switch (activeTab) {
            case 'stats':
                return <StatsTab stats={stats} loading={statsLoading} isMag={isMag} onNavigate={navigateAdmin} />;
            case 'backups':
                return <BackupsTab backups={backups} loading={backupsLoading} refetch={refetchBackups} />;
            case 'files':
                return <FileDrive />;
            case 'users':
                return <UsersTab users={users} loading={usersLoading} refetch={refetchUsers} />;
            case 'regions':
                return <RegionsTab regions={regions} loading={regionsLoading} refetch={refetchRegions} groups={groups} />;
            case 'notifications':
                return (
                    <NotificationsLogTab
                        logs={notificationsLog}
                        loading={notificationsLoading}
                        refetch={refetchNotifications}
                    />
                );
            case 'wp_import':
                return <WpImportTab />;
            case 'rentalClients':
                if (!isMag) return null;
                return (
                    <TherapyClientsTab
                        data={rentalClients}
                        loading={rentalClientsLoading}
                        title="Клиенты Аренды"
                        exportFilePrefix="клиенты_аренда"
                        showTherapists={false}
                        showTherapistInPayments={false}
                    />
                );
            case 'zachetkaGroups':
                if (!isMag) return null;
                return <ZachetkaGroupsTab data={zachetkaGroups} loading={zachetkaGroupsLoading} error={zachetkaGroupsError} />;
            case 'payments':
                return (
                    <PaymentsTab
                        data={paykeeperPayments}
                        loading={paymentsLoading}
                        page={paymentsPage}
                        totalPages={paykeeperPayments?.totalPages ?? 1}
                        total={paykeeperPayments?.total ?? 0}
                        onPageChange={setPaymentsPage}
                        filters={paymentsFilters}
                        onFiltersChange={setPaymentsFilters}
                    />
                );
            case 'zoom':
                if (!isNasledie) return null;
                return <ZoomChannelsTab />;
            case 'mailings':
                if (!isNasledie) return null;
                return <MailingsTab />;
            default:
                return null;
        }
    };

    return (
        <div className="admin-page">
            <div className="admin-header">
                <Settings size={32} />
                <div>
                    <h1>Панель администратора</h1>
                    <p>Управление системой</p>
                </div>
            </div>

            <AdminNavigation mainTabs={mainTabs} groupsSubTabs={groupsSubTabs} therapySubTabs={therapySubTabs} activeTab={activeTab} activeSubTab={activeSubTab} onMain={handleMainTabClick} onSub={handleSubTabClick}/>

            <div className="admin-content">{renderContent()}</div>
        </div>
    );
};

// === КОМПОНЕНТЫ ВКЛАДОК ===

const StatsTab = ({ stats, loading, isMag, onNavigate }) => {
    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    return (
        <div className="stats-tab">
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon users">
                        <Users size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats?.users || 0}</div>
                        <div className="stat-label">Пользователей</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon groups">
                        <Layers size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats?.groups || 0}</div>
                        <div className="stat-label">Всего групп</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon accepted">
                        <Layers size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats?.acceptedGroups || 0}</div>
                        <div className="stat-label">Подтвержденных групп</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon applications">
                        <FileText size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats?.applications || 0}</div>
                        <div className="stat-label">Заявок</div>
                    </div>
                </div>

                {stats?.applicationsByStatus && (
                    <div className="stat-card-wide">
                        <h3>Заявки по статусам</h3>
                        <div className="status-stats">
                            <div className="status-item">
                                <span className="status-label">Новые:</span>
                                <span className="status-value">{stats.applicationsByStatus.pending || 0}</span>
                            </div>
                            <div className="status-item">
                                <span className="status-label">Одобрено:</span>
                                <span className="status-value">{stats.applicationsByStatus.approved || 0}</span>
                            </div>
                            <div className="status-item">
                                <span className="status-label">Связались:</span>
                                <span className="status-value">{stats.applicationsByStatus.contacted || 0}</span>
                            </div>
                            <div className="status-item">
                                <span className="status-label">Отклонено:</span>
                                <span className="status-value">{stats.applicationsByStatus.rejected || 0}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="stats-quick-links">
                <h3>Разделы клиентов</h3>
                <div className="stats-quick-links-row">
                    {isMag ? (
                        <>
                            <button
                                type="button"
                                className="stats-quick-link-btn"
                                onClick={() => onNavigate?.('therapySection', 'clients')}
                            >
                                <Users size={18} />
                                Клиенты магрегатора
                            </button>
                            <button
                                type="button"
                                className="stats-quick-link-btn"
                                onClick={() => onNavigate?.('rentalClients')}
                            >
                                <CreditCard size={18} />
                                Клиенты Аренды
                            </button>
                            <button
                                type="button"
                                className="stats-quick-link-btn"
                                onClick={() => onNavigate?.('zachetkaGroups')}
                            >
                                <GraduationCap size={18} />
                                Группы и студенты
                            </button>
                        </>
                    ) : null}
                    <button
                        type="button"
                        className="stats-quick-link-btn"
                        onClick={() => onNavigate?.('groupsSection', 'students')}
                    >
                        <GraduationCap size={18} />
                        Студенты
                    </button>
                </div>
            </div>
        </div>
    );
};

const UsersTab = ({ users, loading, refetch }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [openedFromLink,setOpenedFromLink]=useState(false);
    useEffect(()=>{const id=new URLSearchParams(window.location.search).get('user');if(id&&!openedFromLink&&users?.length){const target=users.find(u=>String(u.id)===id);if(target){setEditingUser(target);setShowModal(true);setOpenedFromLink(true);}}},[users,openedFromLink]);

    const [search,setSearch]=useState('');
    const [sort,setSort]=useState('name');
    const [userFilters, setUserFilters] = useState({
        role: '',
        status: '',
        is_teacher: '',
        is_therapist: '',
        is_knowledge_editor: '',
        has_email: '',
        has_telegram: '',
    });

    const filteredUsers = useMemo(() => {
        const list = users || [];
        return list.filter((u) => {
            if(search.trim() && ![u.id,u.name,u.last_name,u.email,u.phone].join(' ').toLocaleLowerCase('ru').includes(search.trim().toLocaleLowerCase('ru')))return false;
            if (userFilters.role && u.role !== userFilters.role) return false;
            if (userFilters.status === 'active' && !u.is_active) return false;
            if (userFilters.status === 'inactive' && u.is_active) return false;
            if (userFilters.is_teacher === 'yes' && !u.is_teacher) return false;
            if (userFilters.is_teacher === 'no' && u.is_teacher) return false;
            if (userFilters.is_therapist === 'yes' && !u.is_therapist) return false;
            if (userFilters.is_therapist === 'no' && u.is_therapist) return false;
            if (userFilters.is_knowledge_editor === 'yes' && !u.is_knowledge_editor) return false;
            if (userFilters.is_knowledge_editor === 'no' && u.is_knowledge_editor) return false;
            if (userFilters.has_email === 'yes' && !(u.email && String(u.email).trim())) return false;
            if (userFilters.has_email === 'no' && (u.email && String(u.email).trim())) return false;
            if (userFilters.has_telegram === 'yes' && !u.telegram_chat_id) return false;
            if (userFilters.has_telegram === 'no' && u.telegram_chat_id) return false;
            return true;
        }).sort((a,b)=>sort==='recent'?b.id-a.id:formatUserDisplayName(a).localeCompare(formatUserDisplayName(b),'ru'));
    }, [users, userFilters,search,sort]);

    const getManagedGroups = (user) => {
        const raw = user?.managed_groups;
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string') {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const handleAdd = () => {
        setEditingUser(null);
        setShowModal(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setShowModal(true);
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Вы уверены, что хотите удалить (деактивировать) этого пользователя? Удаление возможно только если пользователь не назначен на события в расписании, не является менеджером групп и не в доступах групп.')) {
            return;
        }

        try {
            const response = await axios.delete(`${API_BASE_URL}/users/${userId}`, {
                withCredentials: true
            });
            toast.success('Пользователь удалён (деактивирован)');
            if (response.data?.warning) {
                toast.warn(response.data.warning, { autoClose: 9000 });
            }
            refetch();
        } catch (error) {
            const data = error.response?.data;
            if (data?.forbidden && data?.summary) {
                toast.error(`Нельзя удалить: ${data.summary}`);
            } else {
                toast.error(data?.message || 'Ошибка удаления');
            }
        }
    };

    const handleExportUsers = () => {
        const list = filteredUsers || [];
        if (list.length === 0) {
            toast.info('Нет пользователей для выгрузки');
            return;
        }

        const headers = [
            'ID',
            'ФИО',
            'Email',
            'Телефон',
            'Роль',
            'Преподаватель',
            'Психолог',
            'Редактор знаний',
            'Групп своих',
            'Свои группы',
            'Кто добавил',
            'Статус'
        ];

        const rows = list.map((user) => {
            const groups = getManagedGroups(user);
            const groupsText = groups
                .map((g) => {
                    const name = g.nomination || g.name || `#${g.id}`;
                    const dir = g.direction_name ? ` (${g.direction_name})` : '';
                    return `${name}${dir}`;
                })
                .join('; ');

            return [
                user.id,
                formatUserDisplayName(user),
                user.email || '',
                user.phone || '',
                user.role === 'admin' ? 'Администратор' : 'Пользователь',
                user.is_teacher ? 'Да' : 'Нет',
                user.is_therapist ? 'Да' : 'Нет',
                user.is_knowledge_editor ? 'Да' : 'Нет',
                user.groups_access_count ?? groups.length,
                groupsText,
                user.created_by_name || user.created_by_email || '',
                user.is_active ? 'Активен' : 'Неактивен'
            ];
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        XLSX.utils.book_append_sheet(wb, ws, 'Пользователи');
        XLSX.writeFile(wb, `пользователи_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Экспорт сохранён');
    };

    const handleSubmit = async (data) => {
        setFormLoading(true);
        try {
            const url = editingUser
                ? `${API_BASE_URL}/users/${editingUser.id}`
                : `${API_BASE_URL}/users`;

            const method = editingUser ? 'put' : 'post';

            // Добавляем роль по умолчанию для новых пользователей
            const payload = editingUser ? data : { ...data, role: 'user' };

            await axios[method](url, payload, { withCredentials: true });

            toast.success(editingUser ? 'Пользователь обновлен' : 'Пользователь создан');
            setShowModal(false);
            setEditingUser(null);
            refetch();
        } catch (error) {
            const d = error.response?.data;
            const fromErrors = Array.isArray(d?.errors)
                ? d.errors.map((e) => e.msg || `${e.path || e.param || ''}: ${e.msg || 'ошибка'}`).join(' ')
                : '';
            toast.error(d?.message || fromErrors || 'Ошибка сохранения');
        } finally {
            setFormLoading(false);
        }
    };



    return (
        <div className="data-table-container user-directory">
            <div className="table-header">
                <div><h2>Пользователи</h2><p className="ud-muted">Профили, профессиональные роли и доступ к календарю</p></div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={handleAdd}>+ Добавить пользователя</button>
                    <button className="btn btn-primary" onClick={handleExportUsers}>Экспорт</button>
                </div>
            </div>
            <div className="ud-search-row"><label>Поиск пользователя<input type="search" placeholder="Имя, фамилия, почта, телефон или номер" value={search} onChange={e=>setSearch(e.target.value)}/></label><label>Сортировка<select value={sort} onChange={e=>setSort(e.target.value)}><option value="name">По имени</option><option value="recent">Сначала новые</option></select></label></div><details className="ud-filter-panel"><summary>Фильтры <span>{Object.values(userFilters).filter(Boolean).length || ''}</span></summary><div className="users-filters-grid">
                <select value={userFilters.role} onChange={(e) => setUserFilters((p) => ({ ...p, role: e.target.value }))}>
                    <option value="">Роль: все</option>
                    <option value="admin">Администратор</option>
                    <option value="user">Пользователь</option>
                </select>
                <select value={userFilters.status} onChange={(e) => setUserFilters((p) => ({ ...p, status: e.target.value }))}>
                    <option value="">Статус: все</option>
                    <option value="active">Активен</option>
                    <option value="inactive">Неактивен</option>
                </select>
                <select value={userFilters.is_teacher} onChange={(e) => setUserFilters((p) => ({ ...p, is_teacher: e.target.value }))}>
                    <option value="">Преподаватель: все</option>
                    <option value="yes">Да</option>
                    <option value="no">Нет</option>
                </select>
                <select value={userFilters.is_therapist} onChange={(e) => setUserFilters((p) => ({ ...p, is_therapist: e.target.value }))}>
                    <option value="">Терапевт: все</option>
                    <option value="yes">Да</option>
                    <option value="no">Нет</option>
                </select>
                <select value={userFilters.is_knowledge_editor} onChange={(e) => setUserFilters((p) => ({ ...p, is_knowledge_editor: e.target.value }))}>
                    <option value="">Редактор знаний: все</option>
                    <option value="yes">Да</option>
                    <option value="no">Нет</option>
                </select>
                <select value={userFilters.has_telegram} onChange={(e) => setUserFilters((p) => ({ ...p, has_telegram: e.target.value }))}>
                    <option value="">Telegram: все</option>
                    <option value="yes">Подключен</option>
                    <option value="no">Не подключен</option>
                </select>
                <select value={userFilters.has_email} onChange={(e) => setUserFilters((p) => ({ ...p, has_email: e.target.value }))}>
                    <option value="">Почта: все</option>
                    <option value="yes">Есть</option>
                    <option value="no">Нет</option>
                </select>
                <button className="btn btn-secondary" onClick={() => setUserFilters({
                    role: '', status: '', is_teacher: '', is_therapist: '', is_knowledge_editor: '', has_email: '', has_telegram: ''
                })}>
                    Сбросить фильтры
                </button>
            </div>
</details>
            <UsersTable users={filteredUsers} onEdit={handleEdit} onDelete={handleDelete}/>
            <Modal
                size="user-profile"
                isOpen={showModal}
                onClose={() => {if(!formLoading)setShowModal(false);}}
                title={editingUser ? 'Редактировать пользователя' : 'Добавить пользователя'}
            >
                <UserEditForm
                    key={editingUser?.id || 'new'}
                    initialData={editingUser}
                    onSubmit={handleSubmit}
                    loading={formLoading}
                />
            </Modal>
        </div>
    );
};

const GroupsTab = ({ groups, loading, refetch }) => {
    const { config } = useConfig();
    const directionTerms = getDirectionTerms(config?.code);
    const [exportFilters, setExportFilters] = useState({
        start_date_from: '',
        start_date_to: '',
        direction_id: '',
        region_id: '',
        recruitment_status: '',
        is_accepted: ''
    });
    const [showExportForm, setShowExportForm] = useState(false);

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const toggleAcceptance = async (groupId, currentStatus) => {
        try {
            await axios.put(
                `${API_BASE_URL}/groups/${groupId}`,
                { is_accepted: !currentStatus },
                { withCredentials: true }
            );
            toast.success(currentStatus ? 'Группа снята с публикации' : 'Группа опубликована');
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка обновления');
        }
    };

    const handleExport = async () => {
        try {
            // Формируем query параметры
            const params = new URLSearchParams();
            Object.keys(exportFilters).forEach(key => {
                if (exportFilters[key]) {
                    params.append(key, exportFilters[key]);
                }
            });

            // Делаем запрос на скачивание файла
            const response = await axios.get(
                `${API_BASE_URL}/admin/export/groups?${params.toString()}`,
                {
                    withCredentials: true,
                    responseType: 'blob'
                }
            );

            // Создаём ссылку для скачивания
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `groups_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success('Файл успешно скачан');
            setShowExportForm(false);
        } catch (error) {
            console.error('Ошибка экспорта:', error);
            toast.error('Ошибка экспорта данных');
        }
    };

    return (
        <div className="data-table-container">
            <div className="table-header">
                <h2>Все группы</h2>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowExportForm(!showExportForm)}
                >
                    📊 Экспорт в CSV
                </button>
            </div>

            {/* Форма фильтров для экспорта */}
            {showExportForm && (
                <div style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #dee2e6'
                }}>
                    <h3 style={{ marginTop: 0 }}>Фильтры для экспорта</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        <div className="form-group">
                            <label>Дата старта от</label>
                            <input
                                type="date"
                                value={exportFilters.start_date_from}
                                onChange={(e) => setExportFilters({ ...exportFilters, start_date_from: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Дата старта до</label>
                            <input
                                type="date"
                                value={exportFilters.start_date_to}
                                onChange={(e) => setExportFilters({ ...exportFilters, start_date_to: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label>{directionTerms.one}</label>
                            <select
                                value={exportFilters.direction_id}
                                onChange={(e) => setExportFilters({ ...exportFilters, direction_id: e.target.value })}
                            >
                                <option value="">Все {directionTerms.manyLower}</option>
                                {[...new Set(groups?.map(g => ({ id: g.direction_id, name: g.direction_name })))]
                                    .filter(d => d.id && d.name)
                                    .map((d, idx) => (
                                        <option key={idx} value={d.id}>{d.name}</option>
                                    ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Регион</label>
                            <select
                                value={exportFilters.region_id}
                                onChange={(e) => setExportFilters({ ...exportFilters, region_id: e.target.value })}
                            >
                                <option value="">Все регионы</option>
                                {[...new Set(groups?.map(g => ({ id: g.region_id, name: g.region_name })))]
                                    .filter(r => r.id && r.name)
                                    .map((r, idx) => (
                                        <option key={idx} value={r.id}>{r.name}</option>
                                    ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Статус набора</label>
                            <select
                                value={exportFilters.recruitment_status}
                                onChange={(e) => setExportFilters({ ...exportFilters, recruitment_status: e.target.value })}
                            >
                                <option value="">Все статусы</option>
                                <option value="open">Набор открыт</option>
                                <option value="closed">Набор закрыт</option>
                                <option value="started">Группа запущена</option>
                                <option value="started_additional">Группа запущена, донабор</option>
                                <option value="completed">Группа завершена</option>
                                <option value="cancelled">Группа не запустилась</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Подтверждение</label>
                            <select
                                value={exportFilters.is_accepted}
                                onChange={(e) => setExportFilters({ ...exportFilters, is_accepted: e.target.value })}
                            >
                                <option value="">Все</option>
                                <option value="true">Только подтверждённые</option>
                                <option value="false">Только неподтверждённые</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                        <button
                            className="btn btn-primary"
                            onClick={handleExport}
                        >
                            📥 Скачать CSV
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setExportFilters({
                                start_date_from: '',
                                start_date_to: '',
                                direction_id: '',
                                region_id: '',
                                recruitment_status: '',
                                is_accepted: ''
                            })}
                        >
                            Очистить фильтры
                        </button>
                    </div>

                    <p style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
                        💡 Совет: Выберите период и {directionTerms.manyLower}, затем в Excel уберите ненужные строки (терапевтические группы и т.д.)
                    </p>
                </div>
            )}
            <table className="data-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Название</th>
                        <th>{directionTerms.one}</th>
                        <th>Регион</th>
                        <th>Менеджер</th>
                        <th>Набор</th>
                        <th>Статус</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {groups?.map(group => (
                        <tr key={group.id}>
                            <td>{group.id}</td>
                            <td>{group.nomination}</td>
                            <td>{group.direction_name || '-'}</td>
                            <td>{group.region_name || '-'}</td>
                            <td>{group.manager_name || '-'}</td>
                            <td>
                                <span className={`status-badge ${group.recruitment_status}`}>
                                    {{
                                        open: 'Набор открыт',
                                        closed: 'Набор закрыт',
                                        started: 'Группа запущена',
                                        started_additional: 'Группа запущена, донабор',
                                        completed: 'Группа завершена',
                                        cancelled: 'Группа не запустилась'
                                    }[group.recruitment_status] || group.recruitment_status}
                                </span>
                            </td>
                            <td>
                                <span className={`status-dot ${group.is_accepted ? 'active' : 'inactive'}`}>
                                    {group.is_accepted ? 'Подтверждена' : 'На модерации'}
                                </span>
                            </td>
                            <td>
                                <button
                                    className="btn-icon"
                                    onClick={() => toggleAcceptance(group.id, group.is_accepted)}
                                    title={group.is_accepted ? 'Снять с публикации' : 'Опубликовать'}
                                >
                                    {group.is_accepted ? <X size={16} /> : <Check size={16} />}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ApplicationsTab = ({ applications, loading, isMag }) => {
    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    return (
        <div className="data-table-container">
            <div className="table-header">
                <h2>Все заявки</h2>
            </div>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Группа</th>
                        <th>Имя</th>
                        <th>Email</th>
                        <th>Телефон</th>
                        <th>Способ связи</th>
                        {isMag && <th>Промокод</th>}
                        <th>Дата</th>
                        <th>Статус</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {applications?.map(app => (
                        <tr key={app.id}>
                            <td>{app.id}</td>
                            <td>{app.group_nomination || app.group_name || '-'}</td>
                            <td>{app.name}</td>
                            <td><a href={`mailto:${app.email}`}>{app.email}</a></td>
                            <td><a href={`tel:${app.phone}`}>{app.phone}</a></td>
                            <td>{app.preferred_contact || '—'}</td>
                            {isMag && <td>{app.promo_code || '—'}</td>}
                            <td>{formatDateShortRu(app.created_at)}</td>
                            <td>
                                <span className={`status-badge ${app.status}`}>
                                    {app.status}
                                </span>
                            </td>
                            <td>
                                <a
                                    href={`/group/${app.group_id}/applications`}
                                    className="btn btn-sm btn-secondary"
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                >
                                    Все заявки группы
                                </a>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const StudentsTab = ({ data, loading }) => {
    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const students = Array.isArray(data) ? data : (data?.students || []);
    const summary = Array.isArray(data)
        ? { students_count: data.length, duplicates_fixed: 0 }
        : (data?.summary || { students_count: students.length, duplicates_fixed: 0 });

    const handleExportStudents = () => {
        const list = students || [];
        if (list.length === 0) {
            toast.info('Нет студентов для выгрузки');
            return;
        }

        const headers = [
            'Имя',
            'Email',
            'Телефон',
            'Кол-во заявок',
            'Последняя заявка',
            'Согласие ПДн',
            'Согласие маркетинг'
        ];

        const rows = list.map((student) => ([
            student.name || '',
            student.email || '',
            student.phone || '',
            student.application_count || 0,
            formatDateShortRu(student.last_application),
            student.has_privacy_consent_application ? 'Да' : 'Нет',
            student.has_marketing_consent_application ? 'Да' : 'Нет'
        ]));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        XLSX.utils.book_append_sheet(wb, ws, 'Студенты');
        XLSX.writeFile(wb, `студенты_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Выгрузка студентов сохранена');
    };

    return (
        <div className="data-table-container">
            <div className="table-header">
                <div>
                    <h2>Студенты (уникальные)</h2>
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                        Всего: <strong>{summary.students_count ?? students.length}</strong>
                        {summary.duplicates_fixed > 0 && (
                            <>
                                {' · '}Исправлено дублей: <strong>{summary.duplicates_fixed}</strong>
                            </>
                        )}
                    </p>
                </div>
                <button
                    className="btn btn-secondary"
                    onClick={handleExportStudents}
                    disabled={!students?.length}
                    title="Скачать студентов в XLSX"
                >
                    Выгрузка студентов
                </button>
            </div>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>Имя</th>
                        <th>Email</th>
                        <th>Телефон</th>
                        <th>Кол-во заявок</th>
                        <th>Последняя заявка</th>
                        <th>Согласие ПДн</th>
                        <th>Согласие маркетинг</th>
                    </tr>
                </thead>
                <tbody>
                    {students?.map((student, idx) => (
                        <tr key={idx}>
                            <td>{student.name}</td>
                            <td>{student.email ? <a href={`mailto:${student.email}`}>{student.email}</a> : '—'}</td>
                            <td>{student.phone ? <a href={`tel:${student.phone}`}>{student.phone}</a> : '—'}</td>
                            <td><strong>{student.application_count}</strong></td>
                            <td>{formatDateShortRu(student.last_application)}</td>
                            <td>{student.has_privacy_consent_application ? 'Да' : 'Нет'}</td>
                            <td>{student.has_marketing_consent_application ? 'Да' : 'Нет'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


const RegionsTab = ({ regions, loading, refetch, groups }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingRegion, setEditingRegion] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const regionFields = [
        { name: 'name', label: 'Название', type: 'text', required: true },
        { name: 'short_name', label: 'Короткое название', type: 'text', required: true }
    ];

    const handleAdd = () => {
        setEditingRegion(null);
        setShowModal(true);
    };

    const handleEdit = (region) => {
        setEditingRegion(region);
        setShowModal(true);
    };

    const handleDelete = async (regionId) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот регион?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/regions/${regionId}`, {
                withCredentials: true
            });
            toast.success('Регион удален');
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка удаления');
        }
    };

    const handleSubmit = async (data) => {
        setFormLoading(true);
        try {
            const url = editingRegion
                ? `${API_BASE_URL}/regions/${editingRegion.id}`
                : `${API_BASE_URL}/regions`;

            const method = editingRegion ? 'put' : 'post';

            await axios[method](url, data, { withCredentials: true });

            toast.success(editingRegion ? 'Регион обновлен' : 'Регион создан');
            setShowModal(false);
            setEditingRegion(null);
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка сохранения');
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <div className="data-table-container">
            <div className="table-header">
                <h2>Регионы</h2>
                <button className="btn btn-primary" onClick={handleAdd}>+ Добавить регион</button>
            </div>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Название</th>
                        <th>Короткое название</th>
                        <th>Групп</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {regions?.map(region => {
                        const groupCount = groups?.filter(g => g.region_id === region.id).length || 0;
                        return (
                            <tr key={region.id}>
                                <td>{region.id}</td>
                                <td>{region.name}</td>
                                <td>{region.short_name}</td>
                                <td><strong>{groupCount}</strong></td>
                                <td>
                                    <button className="btn-icon" onClick={() => handleEdit(region)} title="Редактировать">✏️</button>
                                    <button className="btn-icon" onClick={() => handleDelete(region.id)} title="Удалить">🗑️</button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingRegion ? 'Редактировать регион' : 'Добавить регион'}
                size="small"
            >
                <SimpleForm
                    initialData={editingRegion}
                    fields={regionFields}
                    onSubmit={handleSubmit}
                    onCancel={() => setShowModal(false)}
                    loading={formLoading}
                />
            </Modal>
        </div>
    );
};

const DirectionsTab = ({ directions, loading, refetch, groups }) => {
    const { config } = useConfig();
    const directionTerms = getDirectionTerms(config?.code);
    const [showModal, setShowModal] = useState(false);
    const [editingDirection, setEditingDirection] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const handleAdd = () => {
        setEditingDirection(null);
        setShowModal(true);
    };

    const handleEdit = (direction) => {
        setEditingDirection(direction);
        setShowModal(true);
    };

    const handleDelete = async (directionId) => {
        if (!window.confirm(`Вы уверены, что хотите удалить этот ${directionTerms.oneLower}?`)) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/directions/${directionId}`, {
                withCredentials: true
            });
            toast.success(directionTerms.deleted);
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка удаления');
        }
    };

    const handleSubmit = async (data) => {
        setFormLoading(true);
        try {
            const url = editingDirection
                ? `${API_BASE_URL}/directions/${editingDirection.id}`
                : `${API_BASE_URL}/directions`;

            const method = editingDirection ? 'put' : 'post';

            await axios[method](url, data, { withCredentials: true });

            toast.success(editingDirection ? directionTerms.updated : directionTerms.created);
            setShowModal(false);
            setEditingDirection(null);
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка сохранения');
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <div className="data-table-container">
            <div className="table-header">
                <h2>{directionTerms.many}</h2>
                <button className="btn btn-primary" onClick={handleAdd}>+ Добавить {directionTerms.oneLower}</button>
            </div>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Название</th>
                        <th>Короткое название</th>
                        {config?.code === 'mag' && <th>Тем</th>}
                        {config?.code === 'mag' && <th>Часы</th>}
                        <th>Групп</th>
                        <th>Ссылка</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {directions?.map(direction => {
                        const groupCount = groups?.filter(g => g.direction_id === direction.id).length || 0;
                        return (
                            <tr key={direction.id}>
                                <td>{direction.id}</td>
                                <td>{direction.name}</td>
                                <td>{direction.short_name || '-'}</td>
                                {config?.code === 'mag' && <td>{direction.themes?.length || 0}</td>}
                                {config?.code === 'mag' && <td>{direction.hours_count ?? '—'}</td>}
                                <td><strong>{groupCount}</strong></td>
                                <td>
                                    {direction.page_link && (
                                        <a href={direction.page_link} target="_blank" rel="noopener noreferrer">
                                            Ссылка
                                        </a>
                                    )}
                                </td>
                                <td>
                                    {direction.description && (
                                        <a
                                            href={`${window.location.origin}/directions/${direction.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-icon"
                                            title={`Страница ${directionTerms.manyGen}`}
                                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            🔗
                                        </a>
                                    )}
                                    <button className="btn-icon" onClick={() => handleEdit(direction)} title="Редактировать">✏️</button>
                                    <button className="btn-icon" onClick={() => handleDelete(direction.id)} title="Удалить">🗑️</button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingDirection ? `Редактировать ${directionTerms.oneLower}` : `Добавить ${directionTerms.oneLower}`}
                size="large"
            >
                <DirectionForm
                    direction={editingDirection}
                    onSubmit={handleSubmit}
                    onCancel={() => setShowModal(false)}
                    loading={formLoading}
                />
            </Modal>
        </div>
    );
};

const BackupsTab = ({ backups, loading, refetch }) => {
    const [creating, setCreating] = useState(false);
    const [restoring, setRestoring] = useState(null);

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const handleCreateBackup = async () => {
        if (!window.confirm('Создать новый бекап базы данных?')) {
            return;
        }

        setCreating(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/admin/backups/create`, {}, {
                withCredentials: true
            });
            toast.success(`Бекап создан: ${response.data.backup.filename} (${response.data.backup.sizeMB} MB)`);
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка создания бекапа');
        } finally {
            setCreating(false);
        }
    };

    const handleRestoreBackup = async (filename) => {
        if (!window.confirm(`⚠️ ВНИМАНИЕ! Восстановление из бекапа ПОЛНОСТЬЮ ЗАМЕНИТ текущую базу данных! Все текущие данные будут потеряны!\n\nВосстановить из: ${filename}?`)) {
            return;
        }

        if (!window.confirm('Вы ТОЧНО уверены? Это действие НЕОБРАТИМО!')) {
            return;
        }

        setRestoring(filename);
        try {
            await axios.post(`${API_BASE_URL}/admin/backups/restore`,
                { filename },
                { withCredentials: true }
            );
            toast.success('База данных успешно восстановлена! Страница будет перезагружена...');
            setTimeout(() => window.location.reload(), 2000);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка восстановления из бекапа');
            setRestoring(null);
        }
    };

    const handleDeleteBackup = async (filename) => {
        if (!window.confirm(`Удалить бекап: ${filename}?`)) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/admin/backups/${filename}`, {
                withCredentials: true
            });
            toast.success('Бекап удален');
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка удаления бекапа');
        }
    };

    const handleDownloadBackup = (filename) => {
        window.open(`${API_BASE_URL}/admin/backups/download/${filename}`, '_blank');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="data-table-container">
            <div className="table-header">
                <h2>Бекапы базы данных</h2>
                <button
                    className="btn btn-primary"
                    onClick={handleCreateBackup}
                    disabled={creating}
                >
                    {creating ? 'Создание...' : '💾 Создать бекап'}
                </button>
            </div>

            <div style={{ marginBottom: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#1565c0' }}>
                    ℹ️ <strong>Автоматические бекапы:</strong> создаются каждый день в 03:00. Хранятся последние 30 бекапов.
                </p>
            </div>

            <table className="data-table">
                <thead>
                    <tr>
                        <th>Имя файла</th>
                        <th>Формат</th>
                        <th>Дата создания</th>
                        <th>Размер</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {backups?.length === 0 ? (
                        <tr>
                            <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                Нет доступных бекапов. Создайте первый бекап.
                            </td>
                        </tr>
                    ) : (
                        backups?.map(backup => (
                            <tr key={backup.filename}>
                                <td>
                                    <code style={{ fontSize: '12px', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                                        {backup.filename}
                                    </code>
                                </td>
                                <td>
                                    <span style={{
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        background: backup.format === 'json' ? '#4caf50' : '#2196f3',
                                        color: 'white'
                                    }}>
                                        {backup.format?.toUpperCase() || 'SQL'}
                                    </span>
                                </td>
                                <td>{formatDate(backup.createdAt)}</td>
                                <td>{backup.sizeMB} MB</td>
                                <td>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={() => handleRestoreBackup(backup.filename)}
                                            disabled={restoring}
                                            title="Восстановить из этого бекапа"
                                        >
                                            {restoring === backup.filename ? 'Восстановление...' : '🔄 Восстановить'}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => handleDownloadBackup(backup.filename)}
                                            title="Скачать бекап"
                                        >
                                            💾 Скачать
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleDeleteBackup(backup.filename)}
                                            disabled={restoring}
                                            title="Удалить бекап"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

const PaymentsTab = ({ data, loading, page, totalPages, total, onPageChange, filters, onFiltersChange }) => {
    const queryClient = useQueryClient();
    const [filterDraft, setFilterDraft] = useState(filters);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [checkingPayments, setCheckingPayments] = useState(false);
    const statusMenuRef = React.useRef(null);

    const checkAgregatorPayments = async () => {
        setCheckingPayments(true);
        try {
            const { data: result } = await axios.post(
                `${API_BASE_URL}/admin/paykeeper/check-agregator`,
                {},
                { withCredentials: true, timeout: 120000 }
            );
            if (!result.checked) toast.info(result.message);
            else toast.success(result.message || 'Оплаты магрегатора проверены');
            queryClient.invalidateQueries(['adminPaykeeperPayments']);
            queryClient.invalidateQueries('adminConsultationBookings');
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Не удалось проверить оплаты магрегатора');
        } finally {
            setCheckingPayments(false);
        }
    };

    React.useEffect(() => { setFilterDraft(filters); }, [filters]);

    React.useEffect(() => {
        if (!statusMenuOpen) return undefined;
        const onDocClick = (e) => {
            if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) {
                setStatusMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [statusMenuOpen]);

    const applyFilters = () => {
        onFiltersChange({ ...filterDraft });
        onPageChange(1);
    };

    const emptyFilters = {
        status: [],
        date_from: '',
        date_to: '',
        clientid: '',
        orderid: '',
        search: '',
        amount_from: '',
        amount_to: ''
    };

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const items = data?.items || [];
    const totalCount = total ?? data?.total ?? 0;

    const formatDate = (d) => d ? new Date(d).toLocaleString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
    const formatMoney = (n) => n != null ? Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 2 }) : '—';

    const hasFilters = Object.entries(filters).some(([k, v]) => {
        if (Array.isArray(v)) return v.length > 0;
        return v != null && String(v).trim() !== '';
    });

    const selectedStatuses = Array.isArray(filterDraft.status) ? filterDraft.status : [];
    const statusButtonLabel = selectedStatuses.length === 0
        ? 'Все'
        : selectedStatuses.length === 1
            ? (PAYKEEPER_STATUS_OPTIONS.find((o) => o.value === selectedStatuses[0])?.label || selectedStatuses[0])
            : `Выбрано: ${selectedStatuses.length}`;

    const toggleStatus = (value) => {
        setFilterDraft((prev) => {
            const cur = Array.isArray(prev.status) ? prev.status : [];
            const next = cur.includes(value) ? cur.filter((s) => s !== value) : [...cur, value];
            return { ...prev, status: next };
        });
    };

    const handleExportPayments = async () => {
        if (!hasFilters) {
            toast.warning('Сначала задайте и примените фильтры');
            return;
        }

        setExporting(true);
        try {
            const allItems = [];
            let pageNum = 1;
            let pages = 1;
            const pageLimit = 200;

            do {
                const params = new URLSearchParams({ limit: String(pageLimit), page: String(pageNum) });
                Object.entries(filters).forEach(([k, v]) => {
                    if (Array.isArray(v)) {
                        if (v.length) params.set(k, v.join(','));
                    } else if (v != null && String(v).trim()) {
                        params.set(k, String(v).trim());
                    }
                });

                const response = await axios.get(
                    `${API_BASE_URL}/admin/paykeeper/payments?${params.toString()}`,
                    { withCredentials: true }
                );
                const chunk = response.data?.items || [];
                allItems.push(...chunk);
                pages = response.data?.totalPages || 1;
                pageNum += 1;
            } while (pageNum <= pages);

            if (!allItems.length) {
                toast.info('Нет платежей по применённым фильтрам');
                return;
            }

            const headers = [
                'PK ID',
                'Сумма',
                'Возврат',
                'Клиент',
                'Заказ',
                'Статус',
                'Создан',
                'Проведён',
                'Оповещён'
            ];

            const rows = allItems.map((row) => ([
                row.external_id ?? '',
                row.pay_amount != null ? Number(row.pay_amount) : '',
                row.refund_amount > 0 ? Number(row.refund_amount) : '',
                row.clientid ?? '',
                row.orderid ?? '',
                labelPaykeeperStatus(row.status),
                row.pending_datetime ? formatDate(row.pending_datetime) : '',
                row.obtain_datetime ? formatDate(row.obtain_datetime) : '',
                row.success_datetime ? formatDate(row.success_datetime) : ''
            ]));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            XLSX.utils.book_append_sheet(wb, ws, 'Платежи');
            XLSX.writeFile(wb, `платежи_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Экспорт сохранён (${allItems.length})`);
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Ошибка экспорта платежей');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="data-table-container payments-tab-shell">
            <div className="payments-tab-hero">
                <div className="payments-tab-hero-text">
                    <h2>Платежи PayKeeper</h2>
                    <p>Проверка сверяет консультации со статусом «Подтверждена · ожидает оплаты».</p>
                </div>
                <div className="payments-tab-hero-actions">
                    <button
                        type="button"
                        className="btn payments-btn-accent"
                        onClick={checkAgregatorPayments}
                        disabled={checkingPayments}
                    >
                        {checkingPayments ? 'Проверяем…' : 'Проверить оплаты магрегатора'}
                    </button>
                </div>
            </div>

            <div className="payments-filters-panel">
                <div className="payments-filters-row">
                    <div className="payments-filter-field" ref={statusMenuRef}>
                        <span className="payments-filter-label">Статус</span>
                        <div className="payments-status-multi">
                            <button
                                type="button"
                                className="payments-status-multi-btn"
                                onClick={() => setStatusMenuOpen((o) => !o)}
                            >
                                <span>{statusButtonLabel}</span>
                                <span aria-hidden>{statusMenuOpen ? '▴' : '▾'}</span>
                            </button>
                            {statusMenuOpen && (
                                <div className="payments-status-multi-menu">
                                    {PAYKEEPER_STATUS_OPTIONS.map((opt) => (
                                        <label key={opt.value} className="payments-status-multi-option">
                                            <input
                                                type="checkbox"
                                                checked={selectedStatuses.includes(opt.value)}
                                                onChange={() => toggleStatus(opt.value)}
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <label className="payments-filter-field">
                        <span className="payments-filter-label">Дата от</span>
                        <input
                            type="date"
                            value={filterDraft.date_from}
                            onChange={(e) => setFilterDraft({ ...filterDraft, date_from: e.target.value })}
                            className="payments-filter-input"
                        />
                    </label>
                    <label className="payments-filter-field">
                        <span className="payments-filter-label">Дата до</span>
                        <input
                            type="date"
                            value={filterDraft.date_to}
                            onChange={(e) => setFilterDraft({ ...filterDraft, date_to: e.target.value })}
                            className="payments-filter-input"
                        />
                    </label>
                    <label className="payments-filter-field">
                        <span className="payments-filter-label">Сумма от</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={filterDraft.amount_from}
                            onChange={(e) => setFilterDraft({ ...filterDraft, amount_from: e.target.value })}
                            placeholder="0"
                            className="payments-filter-input"
                        />
                    </label>
                    <label className="payments-filter-field">
                        <span className="payments-filter-label">Сумма до</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={filterDraft.amount_to}
                            onChange={(e) => setFilterDraft({ ...filterDraft, amount_to: e.target.value })}
                            placeholder="∞"
                            className="payments-filter-input"
                        />
                    </label>
                    <label className="payments-filter-field">
                        <span className="payments-filter-label">Клиент (clientid)</span>
                        <input
                            type="text"
                            value={filterDraft.clientid}
                            onChange={(e) => setFilterDraft({ ...filterDraft, clientid: e.target.value })}
                            placeholder="email, ФИО..."
                            className="payments-filter-input payments-filter-input-wide"
                        />
                    </label>
                    <label className="payments-filter-field">
                        <span className="payments-filter-label">Заказ (orderid)</span>
                        <input
                            type="text"
                            value={filterDraft.orderid}
                            onChange={(e) => setFilterDraft({ ...filterDraft, orderid: e.target.value })}
                            placeholder="номер заказа / фамилия"
                            className="payments-filter-input"
                        />
                    </label>
                    <label className="payments-filter-field">
                        <span className="payments-filter-label">Поиск</span>
                        <input
                            type="text"
                            value={filterDraft.search}
                            onChange={(e) => setFilterDraft({ ...filterDraft, search: e.target.value })}
                            placeholder="клиент, заказ, ID"
                            className="payments-filter-input"
                        />
                    </label>
                    <button type="button" className="btn btn-primary payments-btn-accent" onClick={applyFilters}>Применить</button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleExportPayments}
                        disabled={exporting}
                        title="Скачать платежи по применённым фильтрам в Excel"
                    >
                        {exporting ? 'Экспорт…' : 'Экспорт'}
                    </button>
                    {hasFilters && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => { setFilterDraft(emptyFilters); onFiltersChange(emptyFilters); onPageChange(1); }}
                        >
                            Сбросить фильтры
                        </button>
                    )}
                </div>
            </div>

            <div className="payments-summary-bar">
                <span>В базе: <strong>{totalCount}</strong> платежей</span>
                <span className="payments-summary-dot" aria-hidden />
                <span>Страница <strong>{page}</strong> из <strong>{totalPages || 1}</strong></span>
            </div>
            <div className="payments-pager">
                <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Назад</button>
                <button type="button" className="btn btn-secondary" disabled={page >= (totalPages || 1)} onClick={() => onPageChange(page + 1)}>Вперёд</button>
            </div>
            <div className="payments-table-wrap">
            <table className="data-table payments-ledger-table">
                <thead>
                    <tr>
                        <th>PK ID</th>
                        <th>Сумма</th>
                        <th>Возврат</th>
                        <th>Клиент</th>
                        <th>Заказ</th>
                        <th>Статус</th>
                        <th>Создан</th>
                        <th>Проведён</th>
                        <th>Оповещён</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                                Нет данных по выбранным фильтрам или в базе ещё нет платежей.
                            </td>
                        </tr>
                    ) : (
                        items.map((row) => (
                            <tr key={row.id}>
                                <td>{row.external_id}</td>
                                <td>{formatMoney(row.pay_amount)}</td>
                                <td>{row.refund_amount > 0 ? formatMoney(row.refund_amount) : '—'}</td>
                                <td style={{ maxWidth: 200 }} title={row.clientid}>{row.clientid ?? '—'}</td>
                                <td>{row.orderid ?? '—'}</td>
                                <td>
                                    <span className={`status-badge status-${row.status}`}>
                                        {labelPaykeeperStatus(row.status)}
                                    </span>
                                </td>
                                <td>{formatDate(row.pending_datetime)}</td>
                                <td>{formatDate(row.obtain_datetime)}</td>
                                <td>{formatDate(row.success_datetime)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            </div>
        </div>
    );
};

const NotificationsLogTab = ({ logs, loading, refetch }) => {
    const [testEmail, setTestEmail] = useState('');
    const [sendingTest, setSendingTest] = useState(false);

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const handleSendTestEmail = async (e) => {
        e.preventDefault();
        const email = testEmail.trim();
        if (!email) {
            toast.error('Укажите email');
            return;
        }
        setSendingTest(true);
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/admin/test-email`,
                { email },
                { withCredentials: true }
            );
            toast.success(data?.message || `Тестовое письмо отправлено на ${email}`);
            if (refetch) refetch();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Не удалось отправить тестовое письмо');
            if (refetch) refetch();
        } finally {
            setSendingTest(false);
        }
    };

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    return (
        <div className="data-table-container">
            <div className="table-header">
                <h2>Логи уведомлений</h2>
                {refetch && (
                    <button type="button" className="btn btn-secondary" onClick={() => refetch()}>
                        Обновить
                    </button>
                )}
            </div>

            <p style={{ marginBottom: '10px', fontSize: '14px', color: '#555' }}>
                Здесь отображаются последние отправленные уведомления по email и в Telegram.
            </p>

            <form className="matching-admin-settings" onSubmit={handleSendTestEmail}>
                <h3>Тестовое письмо</h3>
                <div className="matching-admin-settings-row">
                    <label className="matching-admin-email">
                        <span>Email получателя</span>
                        <input
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                            disabled={sendingTest}
                        />
                    </label>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={sendingTest}
                    >
                        {sendingTest ? 'Отправка…' : 'Отправить тестовое письмо'}
                    </button>
                </div>
            </form>

            <table className="data-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Время</th>
                        <th>Канал</th>
                        <th>Тип</th>
                        <th>Статус</th>
                        <th>Получатели</th>
                        <th>Заголовок / ошибка</th>
                    </tr>
                </thead>
                <tbody>
                    {(!logs || logs.length === 0) ? (
                        <tr>
                            <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                Пока нет записанных уведомлений.
                            </td>
                        </tr>
                    ) : (
                        logs.map((item) => (
                            <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>{formatDateTime(item.created_at)}</td>
                                <td>
                                    <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        background: item.channel === 'email' ? '#e3f2fd' : '#e8f5e9',
                                        color: item.channel === 'email' ? '#1565c0' : '#2e7d32'
                                    }}>
                                        {item.channel}
                                    </span>
                                </td>
                                <td>
                                    <code style={{ fontSize: '11px', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                                        {item.notification_type}
                                    </code>
                                </td>
                                <td>
                                    <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        background: item.status === 'success' ? '#e8f5e9' : '#ffebee',
                                        color: item.status === 'success' ? '#2e7d32' : '#c62828'
                                    }}>
                                        {item.status === 'success' ? 'OK' : 'ERROR'}
                                    </span>
                                </td>
                                <td>
                                    {item.recipients && item.recipients.length > 0
                                        ? item.recipients.join(', ')
                                        : '-'}
                                </td>
                                <td>
                                    {item.status === 'success'
                                        ? (item.subject || '(без темы)')
                                        : (item.error_message || '(без описания)')}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

const TherapyTopicsTab = ({ topics, loading, refetch }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingTopic, setEditingTopic] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const topicFields = [
        { name: 'name', label: 'Название', type: 'text', required: true },
        { name: 'sort_order', label: 'Порядок сортировки', type: 'number' }
    ];

    const handleAdd = () => {
        setEditingTopic(null);
        setShowModal(true);
    };

    const handleEdit = (topic) => {
        setEditingTopic(topic);
        setShowModal(true);
    };

    const handleDelete = async (topicId) => {
        if (!window.confirm('Вы уверены, что хотите удалить эту тему-запрос?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/therapy-topics/${topicId}`, {
                withCredentials: true
            });
            toast.success('Тема-запрос удалена');
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка удаления');
        }
    };

    const handleSubmit = async (data) => {
        setFormLoading(true);
        try {
            const url = editingTopic
                ? `${API_BASE_URL}/therapy-topics/${editingTopic.id}`
                : `${API_BASE_URL}/therapy-topics`;

            const method = editingTopic ? 'put' : 'post';

            await axios[method](url, data, { withCredentials: true });

            toast.success(editingTopic ? 'Тема-запрос обновлена' : 'Тема-запрос создана');
            setShowModal(false);
            setEditingTopic(null);
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка сохранения');
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <div className="data-table-container">
            <div className="table-header">
                <h2>Темы-запрос</h2>
                <button className="btn btn-primary" onClick={handleAdd}>+ Добавить тему-запрос</button>
            </div>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Название</th>
                        <th>Порядок</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {topics?.map(topic => (
                        <tr key={topic.id}>
                            <td>{topic.id}</td>
                            <td>{topic.name}</td>
                            <td>{topic.sort_order || 0}</td>
                            <td>
                                <button className="btn-icon" onClick={() => handleEdit(topic)} title="Редактировать">✏️</button>
                                <button className="btn-icon" onClick={() => handleDelete(topic.id)} title="Удалить">🗑️</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingTopic ? 'Редактировать тему-запрос' : 'Добавить тему-запрос'}
                size="small"
            >
                <SimpleForm
                    initialData={editingTopic}
                    fields={topicFields}
                    onSubmit={handleSubmit}
                    onCancel={() => setShowModal(false)}
                    loading={formLoading}
                />
            </Modal>
        </div>
    );
};

const QualificationsTab = ({ qualifications, loading, refetch }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingQualification, setEditingQualification] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    const qualificationFields = [
        { name: 'name', label: 'Название', type: 'text', required: true },
        { name: 'sort_order', label: 'Порядок сортировки', type: 'number' }
    ];

    const handleAdd = () => {
        setEditingQualification(null);
        setShowModal(true);
    };

    const handleEdit = (qualification) => {
        setEditingQualification(qualification);
        setShowModal(true);
    };

    const handleDelete = async (qualificationId) => {
        if (!window.confirm('Вы уверены, что хотите удалить эту квалификацию?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/qualifications/${qualificationId}`, {
                withCredentials: true
            });
            toast.success('Квалификация удалена');
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка удаления');
        }
    };

    const handleSubmit = async (data) => {
        setFormLoading(true);
        try {
            const url = editingQualification
                ? `${API_BASE_URL}/qualifications/${editingQualification.id}`
                : `${API_BASE_URL}/qualifications`;

            const method = editingQualification ? 'put' : 'post';

            await axios[method](url, data, { withCredentials: true });

            toast.success(editingQualification ? 'Квалификация обновлена' : 'Квалификация создана');
            setShowModal(false);
            setEditingQualification(null);
            refetch();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка сохранения');
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <div className="data-table-container">
            <div className="table-header">
                <h2>Квалификации</h2>
                <button className="btn btn-primary" onClick={handleAdd}>+ Добавить квалификацию</button>
            </div>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Название</th>
                        <th>Порядок</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {qualifications?.map(qualification => (
                        <tr key={qualification.id}>
                            <td>{qualification.id}</td>
                            <td>{qualification.name}</td>
                            <td>{qualification.sort_order || 0}</td>
                            <td>
                                <button className="btn-icon" onClick={() => handleEdit(qualification)} title="Редактировать">✏️</button>
                                <button className="btn-icon" onClick={() => handleDelete(qualification.id)} title="Удалить">🗑️</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingQualification ? 'Редактировать квалификацию' : 'Добавить квалификацию'}
                size="small"
            >
                <SimpleForm
                    initialData={editingQualification}
                    fields={qualificationFields}
                    onSubmit={handleSubmit}
                    onCancel={() => setShowModal(false)}
                    loading={formLoading}
                />
            </Modal>
        </div>
    );
};

// ─── Заявки на подбор специалиста ─────────────────────────────────────────────

const MatchingApplicationsTab = ({
    applications,
    applicationsLoading,
    settings,
    settingsLoading,
    refetchApplications,
    refetchSettings
}) => {
    const {config}=useConfig();
    const isMag=config?.code==='mag';
    const [administrator,setAdministrator]=useState('');
    const [search,setSearch]=useState('');
    const [studentFilter,setStudentFilter]=useState('');
    const [email, setEmail] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [saving, setSaving] = useState(false);

    React.useEffect(() => {
        if (settings) {
            setAdministrator(String(settings.administrator_id || ''));
            setEmail(settings.notification_email || '');
            setEnabled(Boolean(settings.notifications_enabled));
        }
    }, [settings]);

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API_BASE_URL}/admin/therapist-matching/settings`,
                isMag ? {administrator_id:administrator?Number(administrator):null} : {
                    notification_email: email.trim(),
                    notifications_enabled: enabled
                },
                { withCredentials: true }
            );
            toast.success('Настройки сохранены');
            refetchSettings();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка сохранения настроек');
        } finally {
            setSaving(false);
        }
    };

    const formatMoney = (n) => (n != null ? Number(n).toLocaleString('ru-RU') : '—');
    const formatDate = (d) => (d
        ? new Date(d).toLocaleString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        })
        : '—');

    if (applicationsLoading || settingsLoading) {
        return <div className="loading"><div className="spinner"></div></div>;
    }

    const list = (Array.isArray(applications) ? applications : []).filter(r => (!studentFilter || Boolean(r.is_student)===(studentFilter==='yes')) && [r.first_name,r.last_name,r.email,r.phone,r.request_description].join(' ').toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="admin-consultations">
            <div className="ac-heading">
                <div>
                    <h2>Заявки на подбор</h2>
                    <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>
                        Всего: <strong>{list.length}</strong>
                    </p>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => refetchApplications()}>
                    Обновить
                </button>
            </div>

            <div className="ac-filters ac-filters-compact"><label className="ac-search">Поиск<input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Клиент, контакты или запрос"/></label><label>Часы в зачёт<select value={studentFilter} onChange={e=>setStudentFilter(e.target.value)}><option value="">Все заявки</option><option value="yes">Нужны</option><option value="no">Не нужны</option></select></label><button onClick={()=>{setSearch('');setStudentFilter('');}}>Сбросить</button></div>
            <div className="matching-admin-settings">
                <h3>Уведомления о новых заявках</h3>
                <div className="ac-filters ac-filters-compact">
                    {isMag ? <label className="matching-admin-email"><span>Получатель уведомлений</span><select value={administrator} onChange={e=>setAdministrator(e.target.value)}><option value="">Не назначен</option>{settings?.administrators?.map(u=><option key={u.id} value={u.id}>{u.last_name} {u.name} — {u.email}</option>)}</select></label> : <><label className="matching-admin-email">
                        <span>Email для уведомлений</span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@example.com"
                        />
                    </label>
                    <label className="matching-admin-toggle">
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                        />
                        Получать уведомления
                    </label></>}
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveSettings}
                        disabled={saving}
                    >
                        {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                </div>
            </div>

            <div className="ac-table-wrap"><table className="data-table">
                <thead>
                    <tr>
                        <th>Дата</th>
                        <th>Имя</th>
                        <th>Фамилия</th>
                        <th>Телефон</th>
                        <th>Связь</th>
                        <th>Email</th>
                        <th>Стоимость</th>
                        <th>Студент</th>
                        <th>Запрос</th>
                    </tr>
                </thead>
                <tbody>
                    {list.length === 0 ? (
                        <tr>
                            <td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                                Заявок пока нет
                            </td>
                        </tr>
                    ) : (
                        list.map((row) => (
                            <tr key={row.id}>
                                <td>{formatDate(row.created_at)}{isMag&&<a style={{display:"block",marginTop:8}} href={"/admin/matching/"+row.id}>Открыть заявку и чат →</a>}</td>
                                <td>{row.first_name}</td>
                                <td>{row.last_name}</td>
                                <td>{row.phone}</td>
                                <td>{row.preferred_contact || '—'}</td>
                                <td>{row.email}</td>
                                <td>
                                    {row.price_min != null || row.price_max != null
                                        ? `${formatMoney(row.price_min)} – ${formatMoney(row.price_max)}`
                                        : '—'}
                                </td>
                                <td>{row.is_student ? 'Да' : 'Нет'}</td>
                                <td style={{ maxWidth: 280 }} title={row.request_description || ''}>
                                    {row.request_description
                                        ? (row.request_description.length > 80
                                            ? `${row.request_description.slice(0, 80)}…`
                                            : row.request_description)
                                        : '—'}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table></div>
        </div>
    );
};

// ─── Вкладка импорта из WordPress-дампа ───────────────────────────────────────

const WpImportTab = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [logs, setLogs] = useState([]);

    const handleFileChange = (e) => {
        setFile(e.target.files[0] || null);
        setResult(null);
        setLogs([]);
    };

    const handleImport = async () => {
        if (!file) { toast.warning('Выберите .sql файл дампа'); return; }
        if (!window.confirm(`Импортировать терапевтов из файла «${file.name}»?\nПароль всех импортируемых: 1234\nСтатус профиля: не опубликован`)) return;

        setLoading(true);
        setLogs([]);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('dump', file);

            const response = await axios.post(
                `${API_BASE_URL}/admin/migrate/wp-therapists`,
                formData,
                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
            );

            setResult(response.data);
            setLogs(response.data.logs || []);
            if (response.data.success) {
                toast.success(response.data.message);
            } else {
                toast.error(response.data.message);
            }
        } catch (err) {
            const data = err.response?.data;
            setLogs(data?.logs || [err.message]);
            setResult({ success: false, message: data?.message || err.message });
            toast.error(data?.message || 'Ошибка импорта');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="wp-import-tab">
            <h2>Импорт терапевтов из WordPress-дампа</h2>
            <p className="wp-import-desc">
                Загрузите MySQL-дамп старого магрегатора (<code>.sql</code>). Будут импортированы все
                опубликованные продукты: имя, фото, описания, цена, атрибуты (квалификация, темы,
                город, язык, формат). Email генерируется автоматически (<code>wp_ID@import.local</code>),
                пароль — <code>1234</code>, статус профиля — «не опубликован».
            </p>

            <div className="wp-import-controls">
                <label className="wp-import-file-label">
                    <Upload size={16} />
                    {file ? file.name : 'Выбрать .sql файл'}
                    <input
                        type="file"
                        accept=".sql"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                </label>

                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleImport}
                    disabled={!file || loading}
                >
                    {loading ? 'Импорт…' : 'Запустить импорт'}
                </button>
            </div>

            {result && (
                <div className={`wp-import-summary ${result.success ? 'success' : 'error'}`}>
                    {result.success ? '✓' : '✗'} {result.message}
                    {result.success && (
                        <span className="wp-import-counts">
                            &nbsp;|&nbsp; Добавлено: <strong>{result.inserted}</strong>
                            &nbsp; Пропущено: <strong>{result.skipped}</strong>
                            &nbsp; Ошибок: <strong>{result.errors}</strong>
                        </span>
                    )}
                </div>
            )}

            {logs.length > 0 && (
                <div className="wp-import-logs">
                    <div className="wp-import-logs-header">Лог миграции</div>
                    <pre className="wp-import-logs-body">
                        {logs.join('\n')}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
